import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { apiClient } from './api-client';
import { readLobbyCache, readLobbyCacheUpdatedAt, writeLobbyCache } from '../utils/lobbyQueryCache';
import { buildPlayUrl, stripDeepLinkGameId } from '../utils/playUrl';
import { getStoredUser } from '../utils/auth';

/** How long lobby data is treated as fresh before a background refetch. */
export const LOBBY_STALE_TIME_MS = 60_000;

export interface LobbyDetail {
  id: string;
  tenant_id: string;
  slug: string;
  title: string;
  description: string | null;
  header_title: string | null;
  header_subtitle: string | null;
  banner_image_url: string | null;
  is_default: boolean;
  is_active: boolean;
  tags?: string[];
  images?: LobbyImage[];
  configs?: LobbyConfig[];
}

export interface LobbyGame {
  pickem_id: string;
  display_order: number;
  is_visible: boolean;
  featured_large?: boolean;
  featured_medium?: boolean;
  featured_small?: boolean;
  featured_list_item?: boolean;
  featured_arcade_header?: boolean;
  title_en: string | null;
  title_fr: string | null;
  sport: string | null;
  game_type: string | null;
  prize_text_en: string | null;
  prize_text_fr: string | null;
  text_prize_image_en: string | null;
  text_prize_image_fr: string | null;
  public_url: string | null;
  lobby_tags?: string[];
  /** Pickem contest status from API; null for custom lobby links. */
  status: string | null;
  /** Contest open-for-entry time in UTC ISO format. */
  open_pickem_utc?: string | null;
  external_url?: string | null;
  /** Contest start (picks lock) in UTC ISO format. */
  start_utc?: string | null;
  end_utc?: string | null;
  /**
   * When entry closes for good, UTC ISO — past it nobody can join and the tile is dropped.
   * Survivor: week 1's lock. Distinct from `start_utc`, which drives the "Picks Lock" badge.
   */
  entry_close_utc?: string | null;
  /** Total entries when provided by API (leaderboard count or custom game config). */
  entry_count?: number | null;
  custom_entries?: number | null;
  custom_prize_amount?: number | null;
  jackpot_enabled?: boolean;
  /**
   * Does the VIEWER already hold an entry in this contest? Personalised by the backend from
   * the bearer token, so it is false (or absent, on an older backend) for an anonymous
   * viewer and for a custom lobby link. Drives the Play Now colour on the tile.
   */
  has_entry?: boolean;
  /**
   * Pinned "Persistent" by the lobby section it sits in. Such a tile is served past its
   * contest's start time so that a player who already holds an entry keeps an easy way back
   * into a season-long contest. Only ever acts together with `has_entry` — on its own it
   * keeps nothing visible, and a viewer without an entry still loses the tile at start
   * because there is no entering the contest any more.
   */
  persist_for_entrants?: boolean;
}

export type LobbyImageType = 'large' | 'medium' | 'small' | 'list_item' | 'arcade_header';

export interface LobbyImage {
  id?: string;
  sport?: string;
  game_type?: string;
  gameType?: string;
  locale?: string;
  image_type?: LobbyImageType | string;
  imageType?: LobbyImageType | string;
  image_url?: string;
  imageUrl?: string;
  display_width?: number;
  display_height?: number;
  displayWidth?: number;
  displayHeight?: number;
}

export interface LobbyConfig {
  sport?: string;
  game_type?: string;
  gameType?: string;
  tags?: string[];
  images?: LobbyImage[];
}

export interface LobbySectionSourceResponse {
  sport: string;
  game_type: string;
  tags: string[];
  display_order: number;
}

export interface LobbySectionRuleResponse {
  rule_type: string;
  payload: Record<string, unknown>;
  display_order: number;
}

export interface LobbySectionResponse {
  id: string;
  kind: string;
  title: string | null;
  subtitle: string | null;
  payload: Record<string, unknown>;
  max_games?: number;
  sources?: LobbySectionSourceResponse[];
  rules?: LobbySectionRuleResponse[];
  manual_pickem_ids?: string[];
  games: LobbyGame[];
}

export interface LobbyResponse {
  lobby: LobbyDetail;
  games: LobbyGame[];
  /** Full tenant contest pool for section discovery when sections are configured. */
  tenant_games?: LobbyGame[];
  sections?: LobbySectionResponse[];
  isLive: boolean;
}

/**
 * Ask the backend whether an identifier is one of this tenant's games, and if so build the URL
 * to hand off to. Returns null whenever the hand-off must not happen — not a game, no frontend
 * configured for it in this environment (Game Grid outside local), or the resolve call itself
 * failed — so the caller falls back to the tenant default lobby exactly as it did before.
 *
 * Deliberately swallows its own errors: this runs on a path that has already failed, and a
 * broken resolve must never be worse than the fallback that exists today.
 */
async function resolveGameHandoff(tenantSlug: string, identifier: string): Promise<string | null> {
  try {
    const res = await apiClient.get<{ game_id: string; game_type: string; public_url: string }>(
      `/api/lobbies/${tenantSlug}/resolve/${identifier}`
    );
    const publicUrl = res.data?.public_url;
    if (!publicUrl) return null;

    // Consume the deep-link param before building the target. buildPlayUrl derives `lobbyReturn`
    // from the current URL, and returning the viewer to the URL that launched the game would
    // hand off again the moment they press Back to Lobby — permanently, since the games cache
    // `lobbyReturn` in sessionStorage on first land. Rewriting the entry in place also fixes the
    // browser's own Back button, which would otherwise bounce off the same param.
    const cleanedHref = stripDeepLinkGameId(window.location.href);
    if (cleanedHref !== window.location.href) {
      window.history.replaceState({}, '', cleanedHref);
    }

    const target = buildPlayUrl(publicUrl);
    // A target on the lobby's own path would bounce the viewer straight back here.
    if (!target || target.startsWith(window.location.origin + window.location.pathname)) {
      return null;
    }
    // Deep-link hand-offs are the one place a wrong answer looks like "the main lobby loaded",
    // so leave a breadcrumb rather than failing silently.
    console.log(`[LOBBY] ${identifier} is a ${res.data.game_type} game - handing off`);
    return target;
  } catch {
    return null;
  }
}

/**
 * Fetch the lobby an identifier names, with the fallbacks that make DK's single URL work.
 *
 * Order matters, and each step only runs because the one before it failed:
 *   1. the identifier as a lobby (slug or id) — or the tenant default when there is none
 *   2. the identifier as one of the tenant's GAMES → hand off to that game (phase 2)
 *   3. the tenant default lobby — the long-standing fallback for a slug that does not exist
 *
 * Exported for tests: the ordering is the whole contract, and step 3 masking a failure in
 * step 2 would present as "the main lobby loaded", which is indistinguishable from the bug
 * phase 1 fixed.
 */
/**
 * A stable id for the signed-in viewer, or undefined when nobody is signed in.
 *
 * Used to scope BOTH the react-query key and the sessionStorage cache, because the lobby
 * payload is personalised (`has_entry` per game). It only has to distinguish viewers within
 * one browser session, so whichever id the stored user carries is enough.
 */
function viewerCacheKey(): string | undefined {
  const user = getStoredUser();
  const id = user?.sso_user_id || user?.id;
  return typeof id === 'string' && id ? id : undefined;
}

export async function fetchLobbyForIdentifier(
  tenantSlug: string,
  lobbySlug?: string
): Promise<LobbyResponse> {
  const defaultPath = `/api/lobbies/${tenantSlug}`;
  const path = lobbySlug ? `/api/lobbies/${tenantSlug}/${lobbySlug}` : defaultPath;

  try {
    const res = await apiClient.get<LobbyResponse>(path);
    writeLobbyCache(tenantSlug, lobbySlug, res.data, viewerCacheKey());
    return res.data;
  } catch (error) {
    // If a named lobby slug doesn't exist (e.g. /lobby/testing), fall back to tenant default lobby.
    const status = axios.isAxiosError(error) ? error.response?.status : undefined;
    if (lobbySlug && status === 404) {
      // DK send every game through one URL (DKPLAT-467 phase 2), so an identifier that is not a
      // lobby may be one of the tenant's GAMES. Only reachable once the lobby lookup has already
      // 404ed, so no path that resolves today is affected.
      const handoff = await resolveGameHandoff(tenantSlug, lobbySlug);
      if (handoff) {
        // Never returns: the game replaces the lobby in the frame, exactly as Play Now does.
        window.location.href = handoff;
        return new Promise<LobbyResponse>(() => {});
      }
      const fallback = await apiClient.get<LobbyResponse>(defaultPath);
      writeLobbyCache(tenantSlug, lobbySlug, fallback.data, viewerCacheKey());
      return fallback.data;
    }
    throw error;
  }
}

export function useLobby(tenantSlug: string, lobbySlug?: string) {
  // The viewer is part of the key: the payload now says whether THIS viewer has entered each
  // contest, so a sign-in or sign-out inside one session must fetch a fresh lobby rather than
  // keep serving the previous viewer's answer.
  const viewerKey = viewerCacheKey();
  return useQuery({
    queryKey: ['lobby', tenantSlug, lobbySlug ?? 'default', viewerKey ?? 'anon'],
    queryFn: () => fetchLobbyForIdentifier(tenantSlug, lobbySlug),
    enabled: !!tenantSlug,
    staleTime: LOBBY_STALE_TIME_MS,
    placeholderData: () => readLobbyCache<LobbyResponse>(tenantSlug, lobbySlug, viewerKey),
    initialDataUpdatedAt: () => readLobbyCacheUpdatedAt(tenantSlug, lobbySlug, viewerKey),
    retry: (failureCount, error) => {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return false;
      }
      return failureCount < 2;
    },
    refetchInterval: query => (query.state.error && !query.state.data ? 5000 : false),
    refetchOnReconnect: true,
  });
}
